import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import { useLogo } from '../contexts/LogoContext';
import { 
  Camera, 
  Sparkles, 
  ShieldCheck, 
  ExternalLink, 
  CheckCircle2, 
  AlertCircle, 
  Images, 
  ArrowRight,
  Info,
  Lock,
  Smile,
  X,
  Phone,
  UserCheck,
  Sun,
  Check
} from 'lucide-react';
import { getPhotoSearchStatus, launchPhotoSearchSession } from '../services/photoSearchService';
import toast from 'react-hot-toast';

const FindMyPhotosPage = () => {
  const { currentUser, updateUserProfile } = useAuth();
  const { logoUrl } = useLogo();
  const [isConsentModalOpen, setIsConsentModalOpen] = useState(false);
  const [hasAcknowledgedConsent, setHasAcknowledgedConsent] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);

  // Profile phone requirement state
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [isSavingPhone, setIsSavingPhone] = useState(false);

  const photoSearchStatus = getPhotoSearchStatus();

  const handleOpenConsentModal = () => {
    if (!photoSearchStatus.isConfigured) {
      toast.error('Photo search service is temporarily unavailable. Please check back soon or browse our public gallery.');
      return;
    }

    const phoneDigits = (currentUser?.phone || '').replace(/\D/g, '');
    if (!phoneDigits || phoneDigits.length < 10) {
      setPhoneInput(currentUser?.phone || '');
      setIsPhoneModalOpen(true);
      return;
    }

    setIsConsentModalOpen(true);
  };

  const handleSavePhone = async (e) => {
    e.preventDefault();
    const phoneDigits = phoneInput.replace(/\D/g, '');
    if (!phoneDigits || phoneDigits.length < 10) {
      toast.error('Please enter a valid phone number (at least 10 digits).');
      return;
    }

    try {
      setIsSavingPhone(true);
      await updateUserProfile(currentUser?.uid || currentUser?.id || currentUser?.email, {
        phone: phoneInput.trim()
      });
      setIsPhoneModalOpen(false);
      toast.success('Phone number saved successfully!');
      setIsConsentModalOpen(true);
    } catch (err) {
      console.error('Failed to save phone number:', err);
      toast.error(err.message || 'Failed to update phone number.');
    } finally {
      setIsSavingPhone(false);
    }
  };

  const handleConfirmAndProceed = () => {
    if (!hasAcknowledgedConsent) {
      toast.error('Please confirm your understanding to continue to the photo search platform.');
      return;
    }

    try {
      setIsLaunching(true);
      const result = launchPhotoSearchSession({
        user: currentUser,
        consentGiven: true
      });

      if (result.success) {
        setIsConsentModalOpen(false);
        toast.success('Opening photo search platform in a new tab...');
      } else {
        toast.error(result.error || 'Failed to open photo search service.');
      }
    } catch (err) {
      console.error('Error launching photo search:', err);
      toast.error(err.message || 'An unexpected error occurred.');
    } finally {
      setIsLaunching(false);
    }
  };

  const steps = [
    {
      step: '01',
      title: 'Sign In to BOW',
      description: 'You are signed in securely to your Beats of Washington account.',
      status: 'completed',
      icon: ShieldCheck,
      badge: 'Completed'
    },
    {
      step: '02',
      title: 'Continue to Photo Search',
      description: 'Open our designated personal photo-search partner service with a single click.',
      status: 'current',
      icon: ExternalLink,
      badge: 'Next Step'
    },
    {
      step: '03',
      title: 'Follow Selfie Instructions',
      description: 'Follow the third-party platform’s simple prompts to submit a live or recent selfie.',
      status: 'upcoming',
      icon: Smile,
      badge: 'On Partner Platform'
    },
    {
      step: '04',
      title: 'Discover Your Photos',
      description: 'Instant facial matching highlights photos of you across participating BOW event galleries.',
      status: 'upcoming',
      icon: Sparkles,
      badge: 'Your Memories'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Find My Photos - Ganesh Festival 2026 | Beats of Washington</title>
        <meta 
          name="description" 
          content="Find photos of yourself from Ganesh Festival 2026 using our personal photo-search experience." 
        />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-orange-50/50 via-white to-amber-50/30 py-10 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Beats of Washington Brand Logo */}
          <div className="flex justify-center mb-5">
            <Link 
              to="/"
              className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-orange-500 via-amber-600 to-yellow-600 rounded-full flex items-center justify-center overflow-hidden shadow-xl hover:scale-105 transition-transform duration-300 ring-4 ring-orange-200/60"
              title="Beats of Washington Home"
            >
              {logoUrl ? (
                <img 
                  src={logoUrl} 
                  alt="Beats of Washington Logo" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-white font-extrabold text-2xl sm:text-3xl">B</span>
              )}
            </Link>
          </div>

          {/* Header Breadcrumbs / Tag */}
          <div className="flex items-center justify-center space-x-2 text-xs sm:text-sm font-semibold tracking-wide uppercase text-primary-600 mb-3">
            <span className="flex items-center px-3 py-1 rounded-full bg-orange-100 text-orange-800 border border-orange-200">
              <Camera className="w-3.5 h-3.5 mr-1.5" />
              Event Photo Finder
            </span>
          </div>

          {/* Hero Section */}
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900 tracking-tight">
              Find Your Moments of{' '}
              <span className="bg-gradient-to-r from-orange-600 via-amber-600 to-yellow-600 bg-clip-text text-transparent">
                Ganesh Festival 2026
              </span>
            </h1>
            <p className="mt-4 text-base sm:text-lg text-gray-600 leading-relaxed">
              Looking for photos of yourself from Ganesh Festival 2026? Use our photo search experience to quickly discover your event memories.
            </p>

            {/* Authenticated user greeting */}
            {currentUser && (
              <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-gray-200 shadow-sm text-xs sm:text-sm text-gray-700">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                <span>Signed in as <strong className="text-gray-900">{currentUser.displayName || currentUser.email}</strong></span>
              </div>
            )}
          </div>

          {/* Main CTA Section */}
          <div className="mt-10 sm:mt-12 max-w-2xl mx-auto">
            <div className="relative group bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500 p-1 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300">
              <div className="bg-white rounded-[22px] p-6 sm:p-8 text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-orange-100 to-amber-100 text-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-inner">
                  <Camera className="w-8 h-8 sm:w-10 sm:h-10 text-primary-600 animate-pulse" />
                </div>

                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                  Ready to Search Event Photos?
                </h2>
                <p className="text-sm text-gray-600 max-w-md mx-auto mb-6">
                  Click below to open our personal photo search partner. You will be prompted to follow third-party selfie instructions to match your photos.
                </p>

                {photoSearchStatus.isConfigured ? (
                  <button
                    onClick={handleOpenConsentModal}
                    className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 text-base font-bold rounded-xl text-white bg-gradient-to-r from-orange-600 via-amber-600 to-yellow-600 hover:from-orange-700 hover:via-amber-700 hover:to-yellow-700 shadow-lg hover:shadow-xl hover:scale-[1.02] transform transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-orange-300"
                  >
                    <Sparkles className="w-5 h-5 mr-2.5" />
                    <span>Find My Photos</span>
                    <ArrowRight className="w-5 h-5 ml-2.5 group-hover:translate-x-1 transition-transform" />
                  </button>
                ) : (
                  <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm flex items-start text-left">
                    <AlertCircle className="w-5 h-5 mr-3 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold mb-1">Photo Search Temporarily Unavailable</p>
                      <p className="text-xs text-amber-800">
                        {photoSearchStatus.message} In the meantime, you can explore all event albums in our public gallery.
                      </p>
                    </div>
                  </div>
                )}

                <div className="mt-4 flex items-center justify-center space-x-2 text-xs text-gray-500">
                  <Lock className="w-3.5 h-3.5 text-gray-400" />
                  <span>Opens securely in a new browser tab</span>
                </div>
              </div>
            </div>
          </div>

          {/* How It Works Process Steps */}
          <div className="mt-16 sm:mt-20">
            <div className="text-center max-w-xl mx-auto mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                How It Works
              </h2>
              <p className="text-sm sm:text-base text-gray-600 mt-2">
                Four simple steps from login to discovering your event highlights
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {steps.map((item, idx) => {
                const IconComponent = item.icon;
                const isDone = item.status === 'completed';
                return (
                  <div
                    key={idx}
                    className={`relative rounded-2xl p-6 transition-all duration-200 border ${
                      isDone 
                        ? 'bg-white border-green-200 shadow-md ring-1 ring-green-100' 
                        : 'bg-white border-gray-200/80 shadow hover:shadow-lg hover:border-orange-200'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-2xl font-black text-gray-200 tracking-wider">
                        {item.step}
                      </span>
                      <span className={`inline-flex items-center text-xs font-semibold px-2.5 py-0.5 rounded-full ${
                        isDone
                          ? 'bg-green-100 text-green-800'
                          : item.status === 'current'
                          ? 'bg-orange-100 text-orange-800'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {isDone && <CheckCircle2 className="w-3 h-3 mr-1 text-green-600" />}
                        {item.badge}
                      </span>
                    </div>

                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                      isDone 
                        ? 'bg-green-50 text-green-600' 
                        : 'bg-orange-50 text-orange-600'
                    }`}>
                      <IconComponent className="w-6 h-6" />
                    </div>

                    <h3 className="text-base font-bold text-gray-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Selfie Upload Tips & Guide Card */}
          <div className="mt-12 max-w-4xl mx-auto bg-gradient-to-br from-white via-orange-50/40 to-amber-50/40 rounded-3xl border border-orange-200 p-6 sm:p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-orange-600 text-white flex items-center justify-center shadow-md">
                <Camera className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Selfie Upload Tips for Quick Matching
                </h3>
                <p className="text-xs text-gray-500">
                  Follow these best practices on the partner platform for maximum accuracy
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-xs sm:text-sm">
              <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-xs">
                <div className="w-8 h-8 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center font-bold mb-2.5">
                  <Sun className="w-4 h-4" />
                </div>
                <h4 className="font-bold text-gray-900 mb-1">Good Lighting</h4>
                <p className="text-xs text-gray-600">Ensure your face is clearly and evenly lit with no harsh shadows.</p>
              </div>

              <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-xs">
                <div className="w-8 h-8 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center font-bold mb-2.5">
                  <Smile className="w-4 h-4" />
                </div>
                <h4 className="font-bold text-gray-900 mb-1">Face Camera Directly</h4>
                <p className="text-xs text-gray-600">Take an eye-level, front-facing shot for the most accurate match.</p>
              </div>

              <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-xs">
                <div className="w-8 h-8 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center font-bold mb-2.5">
                  <ShieldCheck className="w-4 h-4" />
                </div>
                <h4 className="font-bold text-gray-900 mb-1">No Face Coverings</h4>
                <p className="text-xs text-gray-600">Remove sunglasses, face masks, or caps before taking your selfie.</p>
              </div>

              <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-xs">
                <div className="w-8 h-8 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center font-bold mb-2.5">
                  <UserCheck className="w-4 h-4" />
                </div>
                <h4 className="font-bold text-gray-900 mb-1">Solo Selfie</h4>
                <p className="text-xs text-gray-600">Make sure only your face is visible in the frame for clean recognition.</p>
              </div>
            </div>
          </div>

          {/* Privacy & Transparency Notice Banner */}
          <div className="mt-8 max-w-4xl mx-auto bg-white rounded-2xl border border-gray-200 p-6 sm:p-8 shadow-sm">
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                <Info className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1 text-sm text-gray-600 space-y-2">
                <h3 className="text-base font-bold text-gray-900">
                  Privacy & Data Handling Transparency
                </h3>
                <p>
                  Beats of Washington partners with a dedicated third-party photo-search platform to provide personal photo matching. 
                  When you use this service:
                </p>
                <ul className="list-disc pl-5 space-y-1 text-xs sm:text-sm text-gray-600">
                  <li>Your selfie is submitted directly to the external photo-search service and is processed according to their privacy policies.</li>
                  <li>Beats of Washington does <strong>not</strong> receive, store, or create biometric facial templates on our servers.</li>
                  <li>No photos or profile pictures are automatically uploaded without your explicit action.</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Public Gallery Cross-Promotion */}
          <div className="mt-10 max-w-4xl mx-auto bg-gradient-to-r from-orange-50 to-amber-50 rounded-2xl border border-orange-200/80 p-6 sm:p-8 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-orange-600 text-white flex items-center justify-center flex-shrink-0 shadow-md">
                <Images className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-bold text-gray-900">
                  Looking for general event highlights?
                </h3>
                <p className="text-xs sm:text-sm text-gray-600">
                  Browse our full public collection of Dhol Tasha, Ganesh Utsav, and community celebrations.
                </p>
              </div>
            </div>
            <Link
              to="/gallery"
              className="inline-flex items-center px-5 py-2.5 rounded-xl bg-white text-gray-900 text-sm font-semibold border border-gray-200 shadow-sm hover:bg-gray-50 hover:border-gray-300 transition-colors whitespace-nowrap"
            >
              <span>Explore Public Gallery</span>
              <ArrowRight className="w-4 h-4 ml-2 text-primary-600" />
            </Link>
          </div>

        </div>
      </div>

      {/* Privacy & Consent Confirmation Modal */}
      {isConsentModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900/75 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 sm:p-7 shadow-2xl border border-gray-100 animate-slide-up relative">
            <button
              onClick={() => setIsConsentModalOpen(false)}
              className="absolute top-5 right-5 text-gray-400 hover:text-gray-600 p-1.5 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-2xl bg-orange-100 text-orange-600 flex items-center justify-center shadow-xs">
                <Camera className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Before Proceeding to Photo Search
                </h3>
                <p className="text-xs text-gray-500">
                  Quick guide for our 3rd-party photo partner
                </p>
              </div>
            </div>

            <p className="text-xs sm:text-sm text-gray-600 leading-relaxed mb-4">
              You are about to be redirected to our partner platform in a new tab. Please review these quick tips:
            </p>

            {/* 3 Streamlined Actionable Points */}
            <div className="bg-gray-50 rounded-2xl p-4 border border-gray-200/70 space-y-3 mb-5 text-xs text-gray-700">
              <div className="flex items-start gap-2.5">
                <span className="w-5 h-5 rounded-full bg-orange-500 text-white font-bold text-[11px] flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                <div>
                  <strong className="text-gray-900">How to Use:</strong> Click <em>Continue to Search</em> below to open the album, then tap the camera icon to start.
                </div>
              </div>

              <div className="flex items-start gap-2.5">
                <span className="w-5 h-5 rounded-full bg-orange-500 text-white font-bold text-[11px] flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                <div>
                  <strong className="text-gray-900">Selfie Upload:</strong> Snap a live selfie or upload a photo. Use good lighting, face front, and remove sunglasses or masks.
                </div>
              </div>

              <div className="flex items-start gap-2.5">
                <span className="w-5 h-5 rounded-full bg-orange-500 text-white font-bold text-[11px] flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
                <div>
                  <strong className="text-gray-900">Photo Matching:</strong> The partner platform automatically scans the album to find and display your matching event photos.
                </div>
              </div>
            </div>

            <label className="flex items-start cursor-pointer text-xs text-gray-800 mb-5 select-none bg-orange-50/50 p-3 rounded-xl border border-orange-200/60">
              <input
                type="checkbox"
                checked={hasAcknowledgedConsent}
                onChange={(e) => setHasAcknowledgedConsent(e.target.checked)}
                className="mt-0.5 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="ml-2.5 font-medium leading-snug">
                I understand how the third-party photo search works and wish to continue.
              </span>
            </label>

            <div className="flex flex-col-reverse sm:flex-row gap-2.5">
              <button
                type="button"
                onClick={() => setIsConsentModalOpen(false)}
                className="w-full sm:w-1/2 py-2.5 px-4 rounded-xl text-xs sm:text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!hasAcknowledgedConsent || isLaunching}
                onClick={handleConfirmAndProceed}
                className="w-full sm:w-1/2 py-2.5 px-4 rounded-xl text-xs sm:text-sm font-bold text-white bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-1.5"
              >
                {isLaunching ? (
                  <span>Opening...</span>
                ) : (
                  <>
                    <span>Continue to Search</span>
                    <ExternalLink className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phone Number Requirement Modal */}
      {isPhoneModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 sm:p-8 shadow-2xl border border-gray-100 relative animate-slide-up">
            <button
              onClick={() => setIsPhoneModalOpen(false)}
              className="absolute top-5 right-5 text-gray-400 hover:text-gray-600 p-1.5 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="w-12 h-12 rounded-2xl bg-orange-100 text-orange-600 flex items-center justify-center mb-4">
              <Phone className="w-6 h-6" />
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-1">
              Phone Number Required
            </h3>
            
            <p className="text-sm text-gray-600 mb-5 leading-relaxed">
              Please provide your contact phone number to complete your profile before proceeding to personal photo search.
            </p>

            <form onSubmit={handleSavePhone} className="space-y-4">
              <div>
                <label htmlFor="requiredPhone" className="block text-xs font-semibold text-gray-700 mb-1">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                    <Phone className="h-4 w-4" />
                  </div>
                  <input
                    id="requiredPhone"
                    type="tel"
                    required
                    value={phoneInput}
                    onChange={(e) => setPhoneInput(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="(425) 555-0199"
                    autoFocus
                  />
                </div>
                <p className="mt-1 text-[11px] text-gray-500">
                  Minimum 10 digits required.
                </p>
              </div>

              <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsPhoneModalOpen(false)}
                  className="w-full sm:w-1/2 py-2.5 px-4 rounded-xl text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSavingPhone}
                  className="w-full sm:w-1/2 py-2.5 px-4 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg transition-all"
                >
                  {isSavingPhone ? 'Saving...' : 'Save & Continue'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default FindMyPhotosPage;
